// Shared admin-page access guard.
//
// Historically every admin page gated access purely on Cognito group membership
// (`Admins` / `PosAdmins`) and bounced everyone else to /admin/login. Under the
// new role model a general Staff user belongs to NO Cognito group — their access
// lives in the authoritative role record exposed by GET /admin/users/me. This
// helper centralizes the decision so pages authorize Staff by their per-tab
// permission instead of locking them out.
//
// Backward compatible and fail-closed:
//   - `Admins` group            -> full access (unchanged).
//   - `PosAdmins` group         -> access to the POS Queue tab (unchanged).
//   - Superadmin/Admin (record) -> full access.
//   - Staff (record)            -> access to a tab only at level >= View.
//   - anything else / errors    -> denied (caller redirects to login).

import { Auth } from 'aws-amplify';
import { levelAtLeast } from '../components/useAdminCapability';

const API_BASE_URL = process.env.REACT_APP_ADMIN_API_URL || process.env.REACT_APP_API_URL || '';

// Cache the capability for the current session so navigating between pages does
// not refetch /admin/users/me on every mount. Keyed by idToken so a new sign-in
// (or token refresh changing identity) invalidates it.
let _meCache = null; // { token, data }

async function _getCapability(session) {
  if (!API_BASE_URL) return null;
  const token = session.getIdToken().getJwtToken();
  if (_meCache && _meCache.token === token) return _meCache.data;
  try {
    const res = await fetch(`${API_BASE_URL}/admin/users/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    _meCache = { token, data };
    return data;
  } catch {
    return null;
  }
}

// Clears the cached capability (call on sign-out).
export function clearAccessCache() {
  _meCache = null;
}

/**
 * resolveTabAccess(session, requiredTab)
 *
 * Returns an access decision for the given Controllable_Tab name. Pass
 * requiredTab = null for pages with no per-tab mapping (e.g. User Management),
 * which then require an admin-level role.
 *
 * Shape: { allowed, role, isAdmin, isPosAdmin, canEdit }
 *   - role: 'admin' | 'pos' | 'staff' | 'none'
 *   - canEdit: true when the caller may edit (Admin/Superadmin, or Staff at Edit)
 */
export async function resolveTabAccess(session, requiredTab) {
  const groups = session.getIdToken().payload['cognito:groups'] || [];
  const isAdminGroup = Array.isArray(groups) && groups.includes('Admins');
  const isPosAdminGroup = Array.isArray(groups) && groups.includes('PosAdmins');

  // Group-based fast path keeps existing Admins behavior identical.
  if (isAdminGroup) {
    return { allowed: true, role: 'admin', isAdmin: true, isPosAdmin: false, canEdit: true };
  }

  // Consult the authoritative capability for everyone else.
  const cap = await _getCapability(session);
  const role = cap?.role;
  const permissions = cap && typeof cap.permissions === 'object' ? cap.permissions : {};

  if (role === 'Superadmin' || role === 'Admin') {
    return { allowed: true, role: 'admin', isAdmin: true, isPosAdmin: false, canEdit: true };
  }

  if (role === 'Staff') {
    if (!requiredTab) {
      return { allowed: false, role: 'staff', isAdmin: false, isPosAdmin: isPosAdminGroup, canEdit: false };
    }
    return {
      allowed: levelAtLeast(permissions[requiredTab], 'View'),
      role: 'staff',
      isAdmin: false,
      isPosAdmin: isPosAdminGroup,
      canEdit: levelAtLeast(permissions[requiredTab], 'Edit'),
    };
  }

  // No authoritative record. Fall back to the legacy PosAdmins group for the
  // POS Queue tab so existing POS-only operators keep working.
  if (isPosAdminGroup && requiredTab === 'POS Queue') {
    return { allowed: true, role: 'pos', isAdmin: false, isPosAdmin: true, canEdit: true };
  }

  return { allowed: false, role: isPosAdminGroup ? 'pos' : 'none', isAdmin: false, isPosAdmin: isPosAdminGroup, canEdit: false };
}

/**
 * requireTabAccess({ navigate, tab })
 *
 * One-call page guard. Confirms an authenticated session, resolves access for
 * the page's Controllable_Tab, and redirects to /admin/login when denied
 * (sending a POS-only operator to the POS queue instead, mirroring prior
 * behavior). Returns null when access is denied/unauthenticated, otherwise
 * `{ user, session, token, access }` for the page to continue.
 */
export async function requireTabAccess({ navigate, tab }) {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const session = await Auth.currentSession();
    const access = await resolveTabAccess(session, tab);

    if (!access.allowed) {
      // A POS-only operator who hit a non-POS page is sent to their queue;
      // everyone else who is not authorized goes back to login.
      if (access.isPosAdmin && tab !== 'POS Queue') {
        navigate('/admin/channels/pos/queue');
      } else {
        navigate('/admin/login');
      }
      return null;
    }

    const token = session.getIdToken().getJwtToken();
    return { user, session, token, access };
  } catch (err) {
    console.error('Page access check failed:', err);
    navigate('/admin/login');
    return null;
  }
}
